import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import ToggleSwitch from "../ToggleSwitch";
import Icon from "../Icon";
import { useAudioEditor } from "../../../context/AudioEditorContext";
import { DEFAULT_TARGET_LUFS, LUFS_PRESETS } from "./constants";
import Dropdown from "../Dropdown";
import styles from "./AudioEditorNormalizeExportOptions.module.css";

export interface AudioEditorNormalizeExportOptionsProps {
    /** `aa` = add-to-project row; `export` = boxed export-modal row (Claude exp-toggle) */
    variant?: "aa" | "export";
}

/**
 * Export-time loudness normalization controls (toggle + LUFS target).
 * Preview playback is unaffected — normalization is applied only when rendering the MP3.
 */
const AudioEditorNormalizeExportOptions: React.FC<AudioEditorNormalizeExportOptionsProps> = ({ variant = "aa" }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { normalizeOnExport, setNormalizeOnExport, targetLufs, setTargetLufs } = useAudioEditor();

    const lufsOptions = useMemo(
        () =>
            LUFS_PRESETS.map((lufs) => ({
                value: String(lufs),
                label:
                    lufs === DEFAULT_TARGET_LUFS
                        ? t("audioEditorNormalizeTargetRecommended", { lufs })
                        : `${lufs} LUFS`,
            })),
        [t],
    );

    return (
        <div className={`${styles.options} ${variant === "export" ? styles.exportVariant : styles.aaVariant}`}>
            <div className={styles.toggleRow}>
                <ToggleSwitch checked={normalizeOnExport} onChange={setNormalizeOnExport} />
                <span className={styles.label}>
                    {t("audioEditorNormalize")}
                    <Tooltip
                        title={<span style={{ whiteSpace: "pre-line" }}>{t("audioEditorNormalizeTooltip")}</span>}
                        arrow
                        placement="top"
                    >
                        <span className={styles.infoIcon} aria-label={t("audioEditorNormalizeTooltip")}>
                            <Icon name="ic-info" className="ic" />
                        </span>
                    </Tooltip>
                </span>
            </div>
            {normalizeOnExport && (
                <div className={styles.targetRow}>
                    <Dropdown
                        label={t("audioEditorNormalizeTarget")}
                        options={lufsOptions}
                        value={String(targetLufs)}
                        onChange={(value) => {
                            if (value === "") return;
                            setTargetLufs(parseFloat(value));
                        }}
                        loading={false}
                        error={false}
                        placeholder={t("audioEditorNormalizeTarget")}
                        isWithoutMargin
                        fullWidth
                    />
                </div>
            )}
        </div>
    );
};

export default AudioEditorNormalizeExportOptions;
